import { Container, Grid, Table, TableBody, TableContainer } from '@mui/material'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import InfiniteScrollLoading from '@/components/UI/InfiniteScrollLoading'
import PageContentStickyTableHeader from '@/components/UI/PageContentStickyTableHeader'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import Error from '@/components/UI/Table/Error'
import NoResults from '@/components/UI/Table/NoResults'
import TableRowLoading from '@/components/UI/Table/RowLoading'
import useUser from '@/hooks/useUser'
import { getUnassignedApplicantsQuery } from '@/lib/apollo'
import type { TransformedUnassignedApplicantData } from '@/lib/apollo/query/getUnassignedApplicants'
import type { Squad } from '@/utils/squad'

import Row from './Row'
import TableHeader from './TableHeader'

export type Props = {
  collapsedMenu: boolean
  squad: Squad
}

const gridSpacing = 4

const Unassigned: FC<Props> = ({ collapsedMenu, squad }) => {
  const [infiniteScrollData, setInfiniteScrollData] = useState<
    TransformedUnassignedApplicantData[]
  >([])
  const [totalResults, setTotalResults] = useState<number>(0)

  const { customerId } = useUser()

  const [fetch, { loading, error }] = getUnassignedApplicantsQuery.useLazyQuery({
    onCompleted: data => {
      const transformedData = getUnassignedApplicantsQuery.toTransformedData(data)

      if (transformedData) {
        setTotalResults(transformedData.total)
        setInfiniteScrollData([...infiniteScrollData, ...transformedData.results])
      }
    },
  })

  const getNextPage = useCallback(() => {
    if (!customerId) {
      return
    }

    fetch({
      variables: {
        customerId,
        squad,
        limit: 100,
        offset: infiniteScrollData.length,
      },
    })
  }, [fetch, customerId, infiniteScrollData.length, squad])

  useEffect(() => {
    if (infiniteScrollData.length === 0) {
      getNextPage()
    }
  }, [getNextPage, customerId, infiniteScrollData.length])

  return (
    <>
      <PageContentStickyTableHeader isNavigationDrawerOpen={!collapsedMenu} yOffset={254}>
        <TableHeader />
      </PageContentStickyTableHeader>
      <Container maxWidth="xl">
        <Grid container spacing={gridSpacing}>
          <PaperedGridItem xs={12}>
            <InfiniteScroll
              dataLength={infiniteScrollData.length}
              next={getNextPage}
              hasMore={infiniteScrollData.length < totalResults}
              loader={<>{!error && <InfiniteScrollLoading />}</>}
              style={{ overflowY: 'hidden' }}
            >
              <TableContainer>
                <Table aria-label="simple table" size="small">
                  <TableHeader />
                  <TableBody>
                    <>
                      {infiniteScrollData.length === 0 && loading ? (
                        <TableRowLoading colSpan={2} />
                      ) : !error && infiniteScrollData.length === 0 ? (
                        <NoResults colSpan={2} />
                      ) : (
                        infiniteScrollData.map(applicant => <Row applicant={applicant} />)
                      )}
                      {error && <Error colSpan={2} />}
                    </>
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          </PaperedGridItem>
        </Grid>
      </Container>
    </>
  )
}

export default Unassigned
